import { inject, observer } from 'mobx-react';
import dynamic from 'next/dynamic';
import { isEmpty } from '@/utils';
import ComponentsErrorBoundary from '@/components/ErrorBoundary/ComponentsErrorBoundary';
import { ProductTracker } from '@/utils/Tracker';
import { EcommerceTracker } from '@/utils/Tracker';
import styles from './index.module.less';
import TradeupTips from './components/TradeupTips';
import AffiliateModal from './components/AffiliateModal';
import gsap from 'gsap';
import ScrollToPlugin from 'gsap/dist/ScrollToPlugin';
import { PrimeBtnProvider } from './usePrimeBtnContext';

// 配件推荐跳转打点相关
import { isComeFromRelatedAccessoriesPage } from '../relatedAccessories/utils';
import { RELATED_ACCESSORIES_ITEM_LIST_NAME } from '../relatedAccessories/constants';
import classNames from 'classnames';
import ProductPageBase from './Base';

const PanoramicDisplay = dynamic(() => import('./components/PanoramicDisplay'));
const TopSide = dynamic(import('./components/TopSide'));
const TopInfo = dynamic(import('./components/TopInfo'));
const InsuranceCheckSerialModal = dynamic(import('./components/InsuranceCheckSerialModal'), { ssr: false });
const ProductSectionComponent = dynamic(import('./components/ProductOverview/Section'), { ssr: false });
const StickyTabBar = dynamic(import('./components/StickyTabBar'));
const ProductPageCampaignFixedVideo = dynamic(import('@/components/CampaignFixedVideo/ProductPageCampaignFixedVideo'), {
  ssr: false,
});
// 全局注册ScrollPlugin
gsap.registerPlugin(ScrollToPlugin);

@inject('area', 'product', 'app', 'abtest')
@observer
class Index extends ProductPageBase {
  constructor(props) {
    super(props);
    this.props.product.fetchProduct();
  }

  componentDidMount() {
    const { productDetail, getProductGifts, curSelectCommodityId } = this.props.product;
    const search = this.props.app.urlQuery;
    this.props.product.fetchExtraBindService(productDetail?.commodities);

    // 获取分销赠品列表
    getProductGifts({ productId: productDetail.id }, (giftsData) => {
      // 落地页带insrc参数显示弹窗
      if ((search.insrc || search.utm_term) && !isEmpty(giftsData)) {
        this.refs['AffiliateModal'] && this.refs['AffiliateModal'].onShow();
      }
    });

    // 虚拟服务商品超期购买 / app引流购买处理
    this.checkOpenBindServiceSerialModal();

    ProductTracker.productPage({
      action: 'product_category',
      label: productDetail.categoryKey,
      productState: this.props.product,
    });

    // 电商推荐行为上报
    const listName = isComeFromRelatedAccessoriesPage() ? RELATED_ACCESSORIES_ITEM_LIST_NAME : 'Product page';
    EcommerceTracker.viewItem({
      productDetail,
      commodityId: curSelectCommodityId,
      listName,
    });
  }

  render() {
    const {
      productDetail,
      curSelectCommodityId,
      InsuranceService,
      overviewType,
      eleStickyTabBarVisible,
      isPanoramicPage,
    } = this.props.product;
    const { InstaUserActive } = this.props.app;
    const { isNeedHidePrice } = productDetail;

    if (isEmpty(productDetail)) return null;

    function EnabledComponent(key) {
      switch (key) {
        case 'InsuranceCheckSerialModal':
          return InsuranceService.modalVisible ? <InsuranceCheckSerialModal /> : null;
        default:
          return null;
      }
    }

    return (
      <PrimeBtnProvider>
        <div className={styles.productDetail}>
          <section className={classNames([styles.topWrap, isPanoramicPage ? styles.topWrapNew : ''])}>
            <div id='pSideL' className={styles.leftWrap}>
              {isPanoramicPage ? (
                <ComponentsErrorBoundary name='product PanoramicDisplay'>
                  <PanoramicDisplay />
                </ComponentsErrorBoundary>
              ) : (
                <ComponentsErrorBoundary name='product TopSide'>
                  <TopSide />
                </ComponentsErrorBoundary>
              )}
            </div>
            <div id='pSideR' className={styles.rightWrap}>
              <ComponentsErrorBoundary name='product TopInfo'>
                <TopInfo />
              </ComponentsErrorBoundary>
            </div>
          </section>

          <ComponentsErrorBoundary name='product Section'>
            <div id='pSecBottom'>
              <ProductSectionComponent overviewType={overviewType} curSelectCommodityId={curSelectCommodityId} />
            </div>
          </ComponentsErrorBoundary>

          {/* 以旧换新通知弹窗 */}
          <TradeupTips />

          {/* 落地页带分销码赠品弹窗 */}
          <AffiliateModal ref='AffiliateModal' />

          {/* 底部stickyBar，如果不展示价格的商品整个不展示 @赵兴艺 */}
          {!isNeedHidePrice && <StickyTabBar />}

          {/* 服务购买序列号校验 */}
          <ComponentsErrorBoundary name='product InsuranceCheckSerialModal'>
            {EnabledComponent('InsuranceCheckSerialModal')}
          </ComponentsErrorBoundary>

          {/* 产品页专用右下角活动宣传视频组件 - 显隐逻辑内部控制 */}
          {InstaUserActive && (
            <ComponentsErrorBoundary name='product ProductPageCampaignFixedVideo'>
              <ProductPageCampaignFixedVideo
                style={{
                  bottom: eleStickyTabBarVisible ? '129px' : '120px',
                  left: '30px',
                }}
                minimizationStyle={{
                  bottom: eleStickyTabBarVisible ? '129px' : '120px',
                  left: '30px',
                }}
              />
            </ComponentsErrorBoundary>
          )}
        </div>
      </PrimeBtnProvider>
    );
  }
}

export async function getServerSideProps(ctx) {
  const { res } = ctx;
  const { productDetail = {} } = res.pageData ?? {};
  const pageProps = {
    initialState: {
      product: {
        productDetail,
        productReviewInfo: productDetail.productReviewInfo ?? {},
        curSelectCommodityId: productDetail.firstCommodityId ?? 0,
      },
    },
  };
  res.pageData.productDetail = null;
  return {
    props: pageProps,
  };
}

export default Index;
